// src/components/calendar/CalendarShadcn.jsx - SMALL FIX: Limit useEvents to creation only
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, ArrowLeft, AlertTriangle } from 'lucide-react';
import { cn } from '../../utils/cn';

// Import PageContainer for proper layout
import { PageContainer } from '../ui';

// Import shadcn componentized system
import CalendarLayoutShadcn from './CalendarLayout/CalendarLayoutShadcn';

// Import shadcn calendar views
import MonthViewShadcn from './CalendarViews/MonthViewShadcn';
import WeekViewShadcn from './CalendarViews/WeekViewShadcn';
import DayViewShadcn from './CalendarViews/DayViewShadcn';

// Import enhanced navigation hook (single source of truth)
import { useCalendarNavigation } from '../../hooks/useCalendarNavigation';

// Import time range hook for localStorage-based time display preference
import { useCalendarTimeRange } from '../../hooks/useCalendarTimeRange';

// Import shadcn EventCreationPanel
import EventCreationPanelShadcn from './EventCreationPanel/EventCreationPanelShadcn';

// ✅ KEEP: Use Events Hook for event creation only (not data loading)
import { useEvents } from '../../hooks/useEvents';
import { useCreationMode } from '../../hooks/useCreationMode';
import { usePreviewEvent } from '../../hooks/usePreviewEvent';
import { usePartners } from '../../hooks/usePartners';

const CalendarShadcn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  // Container ref for responsive detection
  const containerRef = useRef(null);
  const [isCompactLayout, setIsCompactLayout] = useState(false);

  // ✅ ADD: Range tracking to prevent infinite loops
  const [loadedDateRanges, setLoadedDateRanges] = useState([]);

  // Get partner availability from context
  const {
    partnerAvailability,
    availabilityLoading,
    loadPartnerAvailability
  } = usePartners();

  // Calendar date range for availability requests
  const [currentCalendarDateRange, setCurrentCalendarDateRange] = useState({
    start_date: null,
    end_date: null
  });

  // ===============================================
  // EVENT PANEL STATE - MOVED UP TO FIX INITIALIZATION ERROR
  // ===============================================

  const [showEventPanel, setShowEventPanel] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState('');
  const [isCreatingEvent, setIsCreatingEvent] = useState(false);

  // Partner availability state (for partner service booking) - MOVED UP!
  const [partnerForAvailability, setPartnerForAvailability] = useState(null);
  const [viewingPartnerAvailability, setViewingPartnerAvailability] = useState(false);
  const [errorState, setErrorState] = useState('');

  // ===============================================
  // CALENDAR NAVIGATION & VIEW STATE
  // ===============================================

  const navigation = useCalendarNavigation({
    initialView: 'timeGridWeek',
    initialDate: new Date(),
    updateURL: false, // Enable URL synchronization
    location // Pass location for initial state extraction
  });

  // ===============================================
  // TIME RANGE PREFERENCE (localStorage)
  // ===============================================

  const { timeRange, setTimeRange } = useCalendarTimeRange();

  const {
    currentView,
    currentDate,
    currentTitle,
    statusFilter,
    categoryFilter,
    customerFilter,
    getDateRange, // ✅ ADD: Need this for loading events
    Filter
  } = navigation;

  // ===============================================
  // EVENTS DATA & LOADING STATE
  // ===============================================

  const {
    calendarEvents,
    loadEvents,
    calendarLoading,
    eventsError,
    createEvent,
    eventCreationLoading,
    eventCreationError
  } = useEvents();

  // ✅ UPDATED: Smart loading with range tracking
  useEffect(() => {
    const { start, end } = getDateRange();
    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];

    console.log('📅 Calendar: Checking events for range', {
      start: startStr,
      end: endStr,
      view: currentView,
      eventsInCache: calendarEvents.length,
      loadedRanges: loadedDateRanges.length
    });

    // ✅ FIXED: Check if we've already loaded this range (regardless of results)
    const hasLoadedThisRange = loadedDateRanges.some(range =>
      range.start <= startStr && range.end >= endStr
    );

    // Only load if we haven't loaded this range before AND not currently loading
    if (!hasLoadedThisRange && !calendarLoading) {
      console.log('📅 Calendar: Loading events for new range');

      // ✅ Track that we're loading this range BEFORE making the call
      setLoadedDateRanges(prev => [...prev, {
        start: startStr,
        end: endStr,
        loadedAt: new Date().toISOString()
      }]);

      loadEvents({
        start_date: startStr,
        end_date: endStr,
        mode: 'calendar'
      });
    } else {
      console.log('📅 Calendar: Using cached data or already loaded this range');
    }
  }, [currentDate, currentView, getDateRange, calendarLoading, loadEvents, loadedDateRanges]);

  // ✅ ADD: Cleanup effect to reset ranges when needed
  useEffect(() => {
    // Reset loaded ranges if user logs out or changes business
    const handleAuthReset = () => {
      console.log('🧹 Calendar: Clearing loaded date ranges due to auth reset');
      setLoadedDateRanges([]);
    };

    window.addEventListener('auth-state-reset', handleAuthReset);
    return () => window.removeEventListener('auth-state-reset', handleAuthReset);
  }, []);

  // Helper to get current calendar view date range
  const getCurrentCalendarDateRange = useCallback(() => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      start_date: startOfMonth.toISOString().split('T')[0],
      end_date: endOfMonth.toISOString().split('T')[0]
    };
  }, []);

  // Set initial calendar date range
  useEffect(() => {
    setCurrentCalendarDateRange(getCurrentCalendarDateRange());
  }, [getCurrentCalendarDateRange]);

  // 1. Debug the partner availability state
  useEffect(() => {
    console.log('🟣 Partner Availability Debug:', {
      viewingPartnerAvailability,
      partnerForAvailability,
      partnerAvailability,
      availabilityLoading
    });
  }, [viewingPartnerAvailability, partnerForAvailability, partnerAvailability, availabilityLoading]);

  // Helper to extract dates with availability for purple squares
  const getAvailabilityDates = useCallback(() => {
    console.log('🟣 getAvailabilityDates called:', {
      viewingPartnerAvailability,
      partnerForAvailability,
      partnerAvailability
    });

    if (!viewingPartnerAvailability || !partnerForAvailability || !partnerAvailability) {
      console.log('🟣 Early return - missing required data');
      return new Set();
    }

    const partnerId = partnerForAvailability.id;
    console.log('🟣 Looking for partner ID:', partnerId);

    const availabilityData = partnerAvailability[partnerId];
    console.log('🟣 Found availability data:', availabilityData);

    if (!availabilityData) {
      console.log('🟣 No availability data found for partner');
      return new Set();
    }

    const availableDates = new Set();

    // Process each date's availability slots
    Object.entries(availabilityData).forEach(([dateStr, slots]) => {
      console.log(`🟣 Processing date ${dateStr}:`, slots);
      if (slots && slots.length > 0) {
        availableDates.add(dateStr); // dateStr is "2025-08-07" format
        console.log(`🟣 Added available date: ${dateStr}`);
      }
    });

    console.log('🟣 Final available dates:', Array.from(availableDates));
    return availableDates;
  }, [viewingPartnerAvailability, partnerForAvailability, partnerAvailability]);

  // 3. Debug what's being passed to the calendar view
  useEffect(() => {
    const availabilityDates = getAvailabilityDates();
    console.log('🟣 Availability dates being passed to calendar:', {
      count: availabilityDates.size,
      dates: Array.from(availabilityDates),
      viewingPartnerAvailability
    });
  }, [getAvailabilityDates, viewingPartnerAvailability]);

  // ✅ CHANGE 3: Debug log to verify formatted events
  useEffect(() => {
    console.log('📅 Calendar: Events state updated', {
      eventCount: calendarEvents.length,
      firstEvent: calendarEvents[0],
      hasDateObjects: calendarEvents.length > 0 && {
        start: calendarEvents[0]?.start instanceof Date,
        end: calendarEvents[0]?.end instanceof Date
      },
      loading: calendarLoading,
      error: eventsError
    });
  }, [calendarEvents, calendarLoading, eventsError]);

  // ===============================================
  // CREATION MODE & PREVIEW STATE
  // ===============================================

  const { isInCreationMode, enterCreationMode, exitCreationMode } = useCreationMode();

  // Helper to get current timezone offset in ISO format
  const getTimezoneOffset = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset <= 0 ? '+' : '-';
    return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const {
    previewEvent,
    updatePreviewFromForm,
    clearPreview,
    getPreviewData
  } = usePreviewEvent(selectedDateTime, isInCreationMode);

  // ===============================================
  // RESPONSIVE LAYOUT DETECTION
  // ===============================================

  useEffect(() => {
    const checkLayout = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setIsCompactLayout(width < 768);
      }
    };

    checkLayout();
    window.addEventListener('resize', checkLayout);
    return () => window.removeEventListener('resize', checkLayout);
  }, []);

  // ===============================================
  // EVENT-BASED SUCCESS HANDLING
  // ===============================================

  // Track if we've actually tried to create an event
  const [hasTriedCreating, setHasTriedCreating] = useState(false);

  useEffect(() => {
    // Only close panel if we've actually tried creating and it succeeded
    if (!eventCreationLoading && !eventCreationError && isCreatingEvent && hasTriedCreating) {
      handlePanelClose();
      setIsCreatingEvent(false);
      setHasTriedCreating(false);
    }
  }, [eventCreationLoading, eventCreationError, isCreatingEvent, hasTriedCreating]);

  // ===============================================
  // CORE EVENT HANDLERS
  // ===============================================

  // 📅 Universal date/time update handler
  const updateSelectedDateTime = useCallback((date, time) => {
    console.log('🔄 Calendar: Creating ISO datetime:', { date, time });

    // Convert date to YYYY-MM-DD string using local time (not UTC)
    const dateStr = date instanceof Date ?
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : date;

    // Convert hour number to ISO datetime
    let isoDateTime;
    if (typeof time === 'number') {
      // time is hour number (e.g., 10) - create full ISO string
      const timeStr = `${time.toString().padStart(2, '0')}:00:00`;
      const timezoneOffset = getTimezoneOffset();
      isoDateTime = `${dateStr}T${timeStr}${timezoneOffset}`;
    } else if (time instanceof Date) {
      // time is Date object - convert to ISO
      isoDateTime = time.toISOString();
    } else {
      // time is already a string - assume it's ISO format
      isoDateTime = time;
    }

    console.log('🔄 Created ISO datetime:', isoDateTime);
    setSelectedDateTime(isoDateTime);
  }, []);

  // 📅 SINGLE CALENDAR CLICK HANDLER
  const handleTimeSlotClick = useCallback((date, hour) => {
    console.log('📅 Calendar clicked:', { date, hour });

    if (!isInCreationMode) {
      // NOT in creation mode: Enter creation mode
      console.log('📅 Entering creation mode');
      enterCreationMode();
    } else {
      // ALREADY in creation mode: Just update form (preview follows automatically)
      console.log('📅 Updating event location in creation mode');
    }

    // Always update form fields (preview will sync automatically)
    updateSelectedDateTime(date, hour);

    // Always show panel
    setShowEventPanel(true);
    setIsCreatingEvent(false);
  }, [updateSelectedDateTime, isInCreationMode, enterCreationMode]);

  const handleEventClick = useCallback((eventInfo) => {
    console.log('📅 Event clicked:', eventInfo);

    // Don't handle clicks on preview events
    if (eventInfo.isPreview) {
      return;
    }

    // If we're in creation mode, ignore event clicks
    if (isInCreationMode) {
      return;
    }

    // Check if this is a service event (appointment)
    if (eventInfo.event_category === 'service') {
      console.log('📅 Opening appointment details for:', eventInfo.id);
      navigate(`/appointments/${eventInfo.id}`);
    } else {
      // TODO: Handle general events when ready
      console.log('📅 General event clicked (not implemented yet):', eventInfo.title);
    }

  }, [isInCreationMode, navigate]);

  const handleCreateEventClick = useCallback(() => {
    console.log('📅 Create event clicked - entering creation mode');

    // Enter creation mode
    enterCreationMode();

    // Update selected date/time for panel (preview will sync automatically)
    const now = new Date();
    const nowISO = now.toISOString();
    setSelectedDateTime(nowISO);
    setShowEventPanel(true);
    setIsCreatingEvent(false);
  }, [enterCreationMode]);

  // ✅ UPDATED: Removed manual reload
  const handleEventCreated = useCallback((eventData) => {
    setIsCreatingEvent(true);
    setHasTriedCreating(true);

    createEvent(eventData);

    // ✅ REMOVED: Manual reload (EventsProvider handles real-time updates)

  }, [createEvent]);

  const handleFormDateTimeChange = useCallback((dateTime) => {
    // Update form state and preview event
    setSelectedDateTime(dateTime);
  }, []);

  const handlePreviewUpdate = useCallback((formData) => {
    // Update preview event from form changes
    updatePreviewFromForm(formData);
  }, [updatePreviewFromForm]);

  const handlePanelClose = useCallback(() => {
    console.log('📅 Closing event panel');
    setShowEventPanel(false);
    setIsCreatingEvent(false);
    setSelectedDateTime('');

    // Exit creation mode and clear preview
    exitCreationMode();
    clearPreview();
  }, [exitCreationMode, clearPreview]);

  const handlePartnerAvailabilityRequest = useCallback((partner, dateRange) => {
    console.log('📅 Requesting partner availability:', partner.id, dateRange);

    // Use PartnersProvider method to request availability
    loadPartnerAvailability(partner.id, dateRange || currentCalendarDateRange);

    // Set partner state for rendering
    setPartnerForAvailability(partner);
    setViewingPartnerAvailability(true);
  }, [loadPartnerAvailability, currentCalendarDateRange]);

  const handleClearPartnerAvailability = useCallback(() => {
    setPartnerForAvailability(null);
    setViewingPartnerAvailability(false);
  }, []);

  const handleBackToNormalCalendar = useCallback(() => {
    setViewingPartnerAvailability(false);
    setPartnerForAvailability(null);
  }, []);

  // ===============================================
  // MAIN COMPONENT RENDER
  // ===============================================

  return (
    <PageContainer
      padding="default"
      background="gradient"
    >
      {/* Partner Availability Banner */}
      {viewingPartnerAvailability && partnerForAvailability && (
        <div className="mb-6 flex items-center justify-between p-4 bg-purple-600/10 border border-purple-500/20 rounded-xl backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-600/20 rounded-lg">
              <Plus className="w-5 h-5 text-purple-300" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-purple-200">
                Booking with {partnerForAvailability.business_name}
              </h3>
              <p className="text-purple-300/80 text-sm">
                Click on available time slots to schedule a service
              </p>
            </div>
          </div>
          <button
            onClick={handleBackToNormalCalendar}
            className="flex items-center gap-2 px-3 py-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 rounded-lg text-purple-300 hover:text-purple-200 transition-all duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Calendar
          </button>
        </div>
      )}

      {/* ✅ CHANGE 5: Error Display - Updated to show both creation and events errors */}
      {(errorState || eventCreationError || eventsError) && (
        <div className="mb-6 flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-red-300">{errorState || eventCreationError || eventsError}</p>
        </div>
      )}

      {/* Main Calendar Container - Glassmorphism */}
      <div className="relative h-full">
        <div
          ref={containerRef}
          className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 shadow-2xl overflow-hidden"
          style={{
            display: showEventPanel ? 'grid' : 'block',
            gridTemplateColumns: showEventPanel ? '1fr 512px' : undefined,
            height: 'calc(100vh - 110px)'
          }}
        >
          {/* Calendar Views Container */}
          <div className="flex-1 h-full overflow-hidden">
            {console.log('🔍 Events being passed to CalendarLayout:', {
              totalEvents: calendarEvents.length,
              previewEvent: previewEvent,
              isInCreationMode: isInCreationMode,
              eventIds: calendarEvents.map(e => e.id),
              hasPreviewInArray: calendarEvents.some(e => e.isPreview)
            })}
            {console.log('🔍 Preview event details:', {
              previewEvent: previewEvent,
              previewEventId: previewEvent?.id,
              previewEventStart: previewEvent?.start,
              previewEventTitle: previewEvent?.title,
              isInCreationMode: isInCreationMode
            })}
            <CalendarLayoutShadcn
              // Pass shadcn view components
              MonthViewComponent={MonthViewShadcn}
              WeekViewComponent={WeekViewShadcn}
              DayViewComponent={DayViewShadcn}

              // Event handlers
              onTimeSlotClick={handleTimeSlotClick}
              onEventClick={handleEventClick}
              onCreateEvent={handleCreateEventClick}

              // User permissions
              isAdmin={isAdmin}

              // Layout options
              showFilters={false} // Disable filters in layout since we have custom header
              showCreateButton={true} // Enable create button in CalendarHeader
              compact={isCompactLayout}

              // Event panel state
              showEventPanel={showEventPanel}
              previewEvent={previewEvent}

              // ✅ CRITICAL: Pass navigation state to CalendarLayout
              navigationState={navigation}

              // Creation mode props
              isInCreationMode={isInCreationMode}

              // ✅ CHANGE 6: Pass formatted events and loading states to CalendarLayout
              calendarEvents={isInCreationMode && previewEvent ? [...calendarEvents, previewEvent] : calendarEvents}
              loading={calendarLoading}             // Loading state for events
              error={eventsError}                 // Error state for events

              // NEW: Pass availability data for purple squares
              availabilityDates={getAvailabilityDates()}
              viewingPartnerAvailability={viewingPartnerAvailability}

              // NEW: Pass time range preference
              timeRange={timeRange}
              onTimeRangeChange={setTimeRange}

              // Custom styling for glassmorphism container
              className="h-full"
            />
          </div>

          {/* Event Creation Panel - Grid Column */}
          {showEventPanel && (
            <div className="relative h-full overflow-hidden">
              <EventCreationPanelShadcn
                isOpen={showEventPanel}
                onClose={handlePanelClose}
                onEventCreated={handleEventCreated}
                isCreatingEvent={isCreatingEvent || eventCreationLoading}
                selectedDateTime={selectedDateTime}
                onDateTimeChange={handleFormDateTimeChange}
                onPreviewUpdate={handlePreviewUpdate}
                currentCalendarDateRange={currentCalendarDateRange}
                onPartnerAvailabilityRequest={handlePartnerAvailabilityRequest}
                onClearPartnerAvailability={handleClearPartnerAvailability}
              />
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default CalendarShadcn;